import React, { useEffect, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import './restock.css';

function labelStatus(status) {
  return String(status || '').replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function incidentLabel(request) {
  const order = String(request?.incident_order_number || '').trim();
  return order ? `${request?.incident || ''} — ${order}` : request?.incident || '';
}

export default function RestockApprovalPage({ profile }) {
  const params = new URLSearchParams(window.location.search);
  const requestId = String(params.get('id') || '').trim();
  const intent = String(params.get('intent') || '').trim().toLowerCase();
  const [status, setStatus] = useState('loading');
  const [request, setRequest] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState('');

  const api = async (url, options = {}) => {
    const token = await getMemberAccessToken();
    if (!token) throw new Error('Member sign in required.');
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || 'Unable to load restock request.');
    return payload;
  };

  const load = async () => {
    if (!requestId) {
      setError('This approval link is missing the restock request ID.');
      setStatus('error');
      return;
    }
    setError('');
    setStatus('loading');
    try {
      const payload = await api(`/api/member-tracking?mode=restock-request&id=${encodeURIComponent(requestId)}`);
      setRequest(payload?.request || null);
      setCanApprove(Boolean(payload?.canApprove));
      setStatus('ready');
    } catch (err) {
      setError(err?.message || 'Unable to load restock request.');
      setStatus('error');
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async decision => {
    setWorking(decision);
    setError('');
    setMessage('');
    try {
      const payload = await api('/api/member-tracking?mode=restock-decision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: requestId, decision, note }),
      });
      setRequest(payload?.request || request);
      setCanApprove(Boolean(payload?.canApprove ?? canApprove));
      if (payload?.applied === false) {
        setMessage('This request was already decided. No duplicate Logistics order was sent.');
      } else if (decision === 'approve') {
        const delivery = payload?.logisticsEmail;
        if (delivery?.status === 'sent') setMessage('Approved. The Logistics order was sent once and the internal team was copied.');
        else if (delivery?.status === 'not_configured') setMessage('Approved. Logistics email is not configured yet, so no external message was sent.');
        else setMessage(`Approved. Logistics delivery status: ${delivery?.status || 'pending'}.`);
      } else {
        setMessage('Marked Needs Changes. Nothing was sent to Logistics.');
      }
    } catch (err) {
      setError(err?.message || 'Unable to record approval decision.');
    } finally {
      setWorking('');
    }
  };

  return (
    <div className="restock-approval-root">
      <header className="restock-approval-topbar">
        <div>
          <h1>Line Medic Restock Approval</h1>
          <p>Signed in as {profile?.full_name || 'Member'}</p>
        </div>
        <button type="button" onClick={() => window.location.assign('/tracking')}>Deployment Manager</button>
      </header>

      <main className="restock-approval-page">
        {status === 'loading' && <section className="restock-approval-card">Loading restock request…</section>}
        {error && <div className="restock-approval-error">{error}</div>}
        {message && <div className="restock-approval-success">{message}</div>}

        {status === 'ready' && request && (
          <section className="restock-approval-card">
            <div className="restock-approval-heading">
              <div>
                <span className="restock-request-code">{request.request_code || request.id}</span>
                <h2>{incidentLabel(request)}</h2>
                <p>{request.person} · {request.deployment_type || 'Deployment'} · Submitted by {request.submitted_by_name}</p>
              </div>
              <span className={`restock-status restock-status-${request.status}`}>{labelStatus(request.status)}</span>
            </div>

            <div className="restock-approval-meta">
              <div><small>Submitted</small><strong>{formatWhen(request.submitted_at)}</strong></div>
              <div><small>Approval email</small><strong>{labelStatus(request.approval_email_status)}</strong></div>
              <div><small>Logistics send</small><strong>{labelStatus(request.logistics_send_status)}</strong></div>
            </div>

            <div className="restock-order-list">
              {(Array.isArray(request.items) ? request.items : []).map((item, index) => (
                <div className="restock-order-row" key={`${item.assetId}-${item.catalogId}-${index}`}>
                  <div><strong>{item.item}</strong><small>{item.assetName || item.assetId}{item.component ? ` · ${item.component}` : ''}</small></div>
                  <strong>Qty {item.quantity}</strong>
                </div>
              ))}
            </div>

            {request.approved_at && (
              <div className="restock-decision-note"><strong>Approved by {request.approved_by_name}</strong><span>{formatWhen(request.approved_at)}</span></div>
            )}
            {request.denied_at && (
              <div className="restock-decision-note"><strong>Needs changes — {request.denied_by_name}</strong><span>{formatWhen(request.denied_at)}{request.decision_note ? ` · ${request.decision_note}` : ''}</span></div>
            )}

            {request.status === 'pending_approval' && canApprove && (
              <div className="restock-approval-actions">
                <label>Decision note <span>optional</span>
                  <textarea value={note} onChange={event => setNote(event.target.value)} placeholder="Only needed if something should be corrected." rows={3} />
                </label>
                <div>
                  <button type="button" className={`restock-needs-changes${intent === 'needs_changes' ? ' suggested' : ''}`} disabled={Boolean(working)} onClick={() => decide('needs_changes')}>{working === 'needs_changes' ? 'Saving…' : 'Deny / Needs Changes'}</button>
                  <button type="button" className={`restock-approve${intent === 'approve' ? ' suggested' : ''}`} disabled={Boolean(working)} onClick={() => decide('approve')}>{working === 'approve' ? 'Approving…' : 'Approve & Send to Logistics'}</button>
                </div>
                <p>The first valid approval is final for this request. If another approver opens the same email afterward, they will see who already acted and no second Logistics order will be sent.</p>
              </div>
            )}

            {request.status === 'pending_approval' && !canApprove && (
              <div className="restock-approval-info">You can view this request, but your account is not assigned as a Restock Team approver.</div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
